/**
 * Product image zoom  hover preview on gallery, click-to-zoom in modal.
 */
(function () {
  const ZOOM_SCALE = 2;
  const LENS_RATIO = 0.35;
  const GALLERY_HOVER_ZOOM_MQ = window.matchMedia('(min-width: 751px) and (hover: hover)');

  function isGalleryHoverZoomEnabled() {
    return GALLERY_HOVER_ZOOM_MQ.matches;
  }

  class ProductImageZoom {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.product-image-zoom__viewport');
      this.img = root.querySelector('.product-image-zoom__img');
      this.lens = root.querySelector('.product-image-zoom__lens');
      this.isGalleryPreview = root.hasAttribute('data-product-image-zoom-preview');
      this.isZoomed = false;
      this.isPanning = false;

      if (!this.viewport || !this.img) return;

      this.onMove = this.onMove.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onLeave = this.onLeave.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.viewport.addEventListener('mousemove', this.onMove);
      this.viewport.addEventListener('mouseenter', this.onMove);
      if (!this.isGalleryPreview) {
        this.viewport.addEventListener('click', this.onClick);
        this.viewport.addEventListener('mousedown', this.onPointerDown);
        this.viewport.addEventListener('mouseup', this.onPointerUp);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.viewport.addEventListener('touchstart', this.onPointerDown, { passive: true });
        this.viewport.addEventListener('touchend', this.onPointerUp);
        this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: true });
      }
      this.viewport.addEventListener('mouseleave', this.onLeave);
    }

    isPreviewActive() {
      return this.isGalleryPreview && isGalleryHoverZoomEnabled();
    }

    onTouchMove(event) {
      if (!this.isZoomed || !event.touches.length) return;
      const touch = event.touches[0];
      const { x, y } = this.getPercent(touch.clientX, touch.clientY);
      this.applyZoomOrigin(x, y);
    }

    getBounds() {
      return this.viewport.getBoundingClientRect();
    }

    getPercent(clientX, clientY) {
      const rect = this.getBounds();
      const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
      return { x, y };
    }

    applyZoomOrigin(x, y) {
      this.img.style.transformOrigin = `${x}% ${y}%`;
      this.img.style.transform = `scale(${ZOOM_SCALE})`;
    }

    updateLens(clientX, clientY) {
      if (!this.lens || this.isZoomed) return;

      const rect = this.getBounds();
      const lensW = rect.width * LENS_RATIO;
      const lensH = rect.height * LENS_RATIO;
      let left = clientX - rect.left - lensW / 2;
      let top = clientY - rect.top - lensH / 2;

      left = Math.max(0, Math.min(left, rect.width - lensW));
      top = Math.max(0, Math.min(top, rect.height - lensH));

      this.lens.style.width = `${lensW}px`;
      this.lens.style.height = `${lensH}px`;
      this.lens.style.left = `${left}px`;
      this.lens.style.top = `${top}px`;
      this.lens.classList.add('is-visible');
    }

    onMove(event) {
      if (!event.clientX && !event.clientY) return;

      const { x, y } = this.getPercent(event.clientX, event.clientY);

      if (this.isGalleryPreview) {
        if (!this.isPreviewActive()) return;

        this.root.classList.add('is-zoomed');
        this.applyZoomOrigin(x, y);
        if (this.lens) this.lens.classList.remove('is-visible');
        return;
      }

      if (this.isZoomed) {
        this.applyZoomOrigin(x, y);
      } else {
        this.updateLens(event.clientX, event.clientY);
      }
    }

    onClick(event) {
      event.preventDefault();
      this.isZoomed = !this.isZoomed;
      this.root.classList.toggle('is-zoomed', this.isZoomed);

      if (this.isZoomed) {
        const { x, y } = this.getPercent(event.clientX, event.clientY);
        this.applyZoomOrigin(x, y);
        if (this.lens) this.lens.classList.remove('is-visible');
      } else {
        this.reset();
      }
    }

    onLeave() {
      if (this.isGalleryPreview) {
        this.reset();
        return;
      }

      if (!this.isZoomed && this.lens) {
        this.lens.classList.remove('is-visible');
      }
      this.setPanning(false);
    }

    onPointerDown() {
      if (this.isZoomed) this.setPanning(true);
    }

    onPointerUp() {
      this.setPanning(false);
    }

    setPanning(active) {
      this.isPanning = active;
      this.root.classList.toggle('is-panning', active);
    }

    reset() {
      this.isZoomed = false;
      this.setPanning(false);
      this.root.classList.remove('is-zoomed');
      this.img.style.transform = 'scale(1)';
      this.img.style.transformOrigin = 'center center';
      if (this.lens) this.lens.classList.remove('is-visible');
    }

    destroy() {
      this.reset();
      if (!this.viewport) return;
      this.viewport.removeEventListener('mousemove', this.onMove);
      this.viewport.removeEventListener('mouseenter', this.onMove);
      this.viewport.removeEventListener('mouseleave', this.onLeave);
      if (!this.isGalleryPreview) {
        this.viewport.removeEventListener('click', this.onClick);
        this.viewport.removeEventListener('mousedown', this.onPointerDown);
        this.viewport.removeEventListener('mouseup', this.onPointerUp);
        this.viewport.removeEventListener('touchstart', this.onPointerDown);
        this.viewport.removeEventListener('touchend', this.onPointerUp);
        this.viewport.removeEventListener('touchmove', this.onTouchMove);
      }
    }
  }

  const instances = new WeakMap();

  function initZoom(root) {
    if (!root || instances.has(root)) return instances.get(root);
    const instance = new ProductImageZoom(root);
    instances.set(root, instance);
    return instance;
  }

  function initAllZoom(scope) {
    (scope || document).querySelectorAll('[data-product-image-zoom]').forEach((el) => initZoom(el));
  }

  function initGalleryZoom() {
    const gallery = document.querySelector('.overview-product-slider');
    if (!gallery) return;

    initAllZoom(gallery);

    if (typeof jQuery === 'undefined') return;
    const $for = jQuery('.overview-product-slider .product-slider-for');

    if (!$for.length) return;

    $for.off('beforeChange.productImageZoom');
    $for.on('beforeChange.productImageZoom', function (_event, _slick, current) {
      const slide = $for.find('.slick-slide').not('.slick-cloned').get(current);
      if (slide) resetZoomInSlide(slide);
    });
  }

  function resetZoomInSlide(slide) {
    if (!slide) return;
    const root = slide.querySelector('[data-product-image-zoom]');
    const instance = root ? instances.get(root) : null;
    if (instance) instance.reset();
  }

  window.ProductImageZoom = {
    init: initZoom,
    initAll: initAllZoom,
    initGallery: initGalleryZoom,
    resetInSlide: resetZoomInSlide,
    resetAllInModal(modal) {
      if (!modal) return;
      modal.querySelectorAll('[data-product-image-zoom]').forEach((el) => {
        const instance = instances.get(el);
        if (instance) instance.reset();
      });
    },
  };

  function resetGalleryPreviewZoom() {
    document
      .querySelectorAll('.overview-product-slider [data-product-image-zoom-preview]')
      .forEach((el) => {
        const instance = instances.get(el);
        if (instance) instance.reset();
      });
  }

  function initOnReady() {
    initGalleryZoom();
    initAllZoom(document.getElementById('imageModal'));

    const onHoverZoomMediaChange = () => {
      if (!isGalleryHoverZoomEnabled()) resetGalleryPreviewZoom();
    };

    if (GALLERY_HOVER_ZOOM_MQ.addEventListener) {
      GALLERY_HOVER_ZOOM_MQ.addEventListener('change', onHoverZoomMediaChange);
    } else {
      GALLERY_HOVER_ZOOM_MQ.addListener(onHoverZoomMediaChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOnReady);
  } else {
    initOnReady();
  }
})();
